/**
 * firebaseMessaging.js — Notificaciones push para WEB / PWA
 *
 * Este módulo usa Firebase Messaging con service worker y VAPID key.
 * En la app nativa de Android, este módulo NO se ejecuta —
 * la lógica nativa está en src/utils/nativeNotifications.js
 */
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { doc, setDoc } from "firebase/firestore";
import { db } from "./firebase";
import { isNativePlatform } from "./utils/nativeNotifications";

const VAPID_KEY =
  "BBRQNQ7rU7ds_GFXUaW3uRPgdIOSRsGUmvbApQL9EwVKfOPg4NExVJY14Q-dBUgXI81u181ajtO1EpPViaOxZ0Q";

/**
 * Solicita permiso y devuelve el token FCM (solo en web/PWA).
 * En app nativa devuelve null — el token lo gestiona nativeNotifications.js
 */
export const requestPermissionAndGetToken = async () => {
  // En la app nativa Android el service worker no existe — salir silenciosamente
  if (isNativePlatform()) return null;

  try {
    const messaging = getMessaging();
    const permission = await Notification.requestPermission();

    if (permission !== "granted") return null;

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    return token || null;
  } catch (error) {
    console.error("[WebFCM] Error obteniendo token:", error);
    return null;
  }
};

/**
 * Guarda el token FCM del usuario en Firestore.
 * Compartido por web y nativo — ambos guardan en la misma colección.
 */
export const saveTokenToFirestore = async (userId, token) => {
  if (!userId || !token) return;
  try {
    await setDoc(doc(db, "fcmTokens", userId), {
      token,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("[FCM] Error guardando token en Firestore:", error);
  }
};

/**
 * Escucha mensajes en PRIMER PLANO (solo web/PWA).
 * En app nativa, el listener equivalente es 'pushNotificationReceived' de Capacitor.
 * @returns función de cleanup para el useEffect
 */
export const onMessageListener = (callback) => {
  if (isNativePlatform()) return () => {};
  try {
    const messaging = getMessaging();
    return onMessage(messaging, (payload) => {
      if (callback) callback(payload);
    });
  } catch (error) {
    console.error("[WebFCM] Error en onMessageListener:", error);
    return () => {};
  }
};
